<!--
  Top app-nav strip — mirrors DST `statement-tracker.component.html`:
    - "Submission List" anchor (always orange, like DST's "Statement
      Tracker" home link).
    - Vertical-more separator (3 dots between the anchor and the open
      tabs — purely visual, matches the legacy screenshot UI).
    - One `<li class="tabs">` per opened submission, each with an inline
      `×` close button and an `.active` highlight when selected.

  `selectedTab === 0` ↔ Submission List, `selectedTab === i + 1` ↔
  the i-th open submission. The same numbering used by
  `selectedTabChange()` and `closeSubmissionTab()` is preserved so the
  Router.navigate / `submissionService.activateSubmissionByIndex` flow
  in the TypeScript class keeps working unchanged.
-->
<div class="efile-shell">
  <div class="app-nav efile-app-nav">
    <ul>
      <li>
        <!--
          "Submission List" anchor. Inline orange background so it always
          stands out as the home link, matching DST's "Statement Tracker"
          anchor. Clicking it navigates back to /submissions-dasboard/list
          via `selectedTabChange(0)`.
        -->
        <a class="anchor"
           style="background-color: #c25423; color: #fff;"
           [class.active]="selectedTab === 0"
           (click)="selectedTabChange(0)">
          Submission List
        </a>
      </li>
      <li class="separator">
        <span class="Appkit4-icon icon-vertical-more-outline"></span>
      </li>
      @for (submission of selectedSubmissions; track $index; let i = $index) {
<li class="tabs"
         >
        <!--
          Compact tab title `{TAX_YEAR}-{TAX_PAYER}` (e.g. `2024-CC2005`)
          with the full TAX_PAYER_NAME exposed via `ap-tooltip` so users
          can still see the long name without bloating the strip.

          The inline `×` calls `closeSubmissionTab($event, i)`, which
          already invokes `event.stopPropagation()` so the parent `<a>`
          click does NOT activate the tab being closed.
        -->
        <a [class.active]="selectedTab === i + 1"
           ap-tooltip
           [tooltipContent]="submission.TAX_PAYER_NAME"
           [direction]="'bottom'"
           [tooltipId]="'submission-tab-' + i"
           ariaDescribedby="submission-taxpayer-name"
           (click)="selectedTabChange(i + 1)">
          {{ submission.TAX_YEAR }}-{{ submission.TAX_PAYER }}
          <span class="Appkit4-icon icon-close-outline"
                (click)="closeSubmissionTab($event, i)"></span>
        </a>
      </li>
}
    </ul>
  </div>

  <!--
    Submission List body — mounted when `selectedTab === 0`. We still use
    `*ngIf` (not display:none) so the inner gizmoGrid scaffold doesn't
    keep firing layout calculations while the user is inside an open
    submission tab. The `EfileGridCacheService` rehydrates the grid from
    cache on remount, so this round-trip is effectively free.

    `list-pane--hidden` is added (NOT `*ngIf`-removed) when the URL is
    `/submission/:id/...` while the cached-tab fast path is still
    settling — e.g. the brief window between a cross-config navigation
    (clicking a return-review sub-tab → `/submission/:id/return-review/<leaf>`)
    and `capturePendingDeepLink` flipping `selectedTab` to the cached
    submission. Without that visual gate, the fresh
    `SubmissionDataComponent` instance defaults `selectedTab` to 0 and
    the list pane briefly paints over the submission tab the user
    actually requested — exactly the "URL is fine but the page redirects
    to submission list" symptom.

    We use `visibility: hidden` instead of `display: none`/`*ngIf` so
    gizmoGrid keeps a properly-sized host during true deep-links (paste
    `/submission/:id/...` with an empty tab cache → slow path mounts
    this grid offscreen so `applyPendingDeepLink` can resolve the row).
    `display: none` would break gizmoGrid's column-width math, the same
    hazard we worked around in the return-review per-step host.
  -->
  @if (selectedTab === 0) {
<div class="ap-pl-spacing-3 ap-pr-spacing-3 content-box list-pane"
      
       [class.list-pane--hidden]="isSubmissionUrl && !deepLinkApplied">
    <!--
      Legacy filter bar from `ui/app/components/efile/efileElements/list-tpl.html`:
      refresh icon → tax-year dropdown → federal/state dropdown → Filter button.
      The refresh icon spins while `[efileGrid]` fetches data so the user
      gets the same visual feedback as the jQuery `fa-spinner` in the
      legacy template.
    -->
    <div class="ap-pb-spacing-2 ap-pt-spacing-2 filters">
      <i class="fa fa-refresh gt-refresh"
         [class.spinning]="loading"
         (click)="refresh()"
         title="Refresh Data"></i>

      <ap-dropdown class="efile-filter-dd" [list]="taxYearItems" [selectType]="'single'"
                  [title]="'Tax Year'" [hideTitleOnInput]="true" [inputAriaLabel]="'Tax Year'"
                  [(ngModel)]="selectedTaxYearItem" (onSelect)="taxYearSelected($event)">
        @for (item of taxYearItems; track $index) {
          <ap-dropdown-list-item #dropdownListItem [item]="item"
            [ariaLabel]="setDropdownAriaLabel(item, 'Tax Year')"
            (onSelectItem)="onTaxYearSelectItem($event)"></ap-dropdown-list-item>
        }
      </ap-dropdown>

      @if (showTypeFilter) {
<ap-dropdown
                  class="efile-filter-dd type-filter-dd"
                  [list]="submissionTypeItems"
                  [selectType]="'single'"
                  [title]="'Submission Type'"
                  [hideTitleOnInput]="true"
                  [inputAriaLabel]="'Submission Type'"
                  [(ngModel)]="selectedSubmissionTypeItem"
                  (onSelect)="submissionTypeSelected($event)">
        @for (item of submissionTypeItems; track $index) {
          <ap-dropdown-list-item #dropdownListItem [item]="item"
            [ariaLabel]="setDropdownAriaLabel(item, 'Submission Type')"
            (onSelectItem)="onSubmissionTypeSelectItem($event)"></ap-dropdown-list-item>
        }
      </ap-dropdown>
}

      <button type="button" class="filter-btn" (click)="filterClick()">Filter</button>
    </div>

    <!--
      gizmoGrid host — rendered through `EfileGridDirective`
      (see .cursor/rules/efile-grid-directive.mdc). The directive owns
      init / data load / cache / destroy. This component only supplies
      `gridConfig` and listens for callbacks.

      The grid is mounted on first Filter click (or on deep-link) so the
      empty-state is fully hidden until the user opts in — this mirrors
      the legacy list-tpl workflow and avoids the empty gizmoGrid scaffold
      flashing on page entry.
    -->
    @if (filterApplied) {

      <!--
        Wrap the directive host in a positioning shell so we can overlay
        a centered spinner while gizmoGrid finishes its first load. The
        directive injects GRID_SCAFFOLD_HTML synchronously on mount which
        paints an empty toolbar (Export to Excel, Comments, Grid Actions,
        color picker, scrollbars) before the data lands. Hiding the
        scaffold via `.gg-loading` keeps the section blank until the
        first dataset is rendered.
      -->
      <div class="gg-host-wrap" [class.gg-loading]="loading || !gridFirstLoaded">
        <div efileGrid
             [gridConfig]="gridConfig"
             (gridCallback)="onGridCallback($event)"
             (gridDataLoaded)="onGridDataLoaded($event)"
             (loadingStateChange)="loading = $event"
             class="gizmo-grid gg-host"></div>

        @if (loading || !gridFirstLoaded) {
<div class="gg-host-loader">
          <i class="fa fa-spinner fa-pulse" aria-hidden="true"></i>
          <span>Loading submissions…</span>
        </div>
}
      </div>
    
} @else {

      <div class="filter-hint">
        <i class="fa fa-filter"></i>
        <span>Select a tax year and submission type, then click <strong>Filter</strong> to load submissions.</span>
      </div>
    
}

    

  </div>
}

  <!--
    Centered "Loading submission…" overlay shown while the URL targets a
    `/submission/:id/...` path but `selectedTab` is still 0 — i.e. either
    a true deep-link round-trip (slow path: grid load → `applyPendingDeepLink`
    → tab activation) or the brief frame after a cross-config navigation
    where the cached-tab fast path hasn't yet flipped `selectedTab`. Sits
    above the visually-hidden list-pane so the user never sees the
    Submission List grid where a submission detail tab is expected.
  -->
  @if (selectedTab === 0 && isSubmissionUrl && !deepLinkApplied) {
<div class="submission-deep-link-loader"
      >
    <i class="fa fa-spinner fa-pulse" aria-hidden="true"></i>
    <span>Loading submission…</span>
  </div>
}

  <!--
    Submission detail body — one pane per opened tab, mounted only when
    that tab is active. Mirrors the previous `<mat-tab-body>` lazy
    behavior so each submission's `<submission-detail>` (and its inner
    grids / breadcrumb / sidebar) initialize once when their tab is
    selected, and so the shared `[subState]` / `[childSubState]`
    @Input pair never bleeds across submissions.
  -->
  @for (submission of selectedSubmissions; track $index; let i = $index) {

    @if (selectedTab === i + 1) {
<div class="submission-pane">
      <!--
        Breadcrumb under the tab strip (matches the screenshot:
        `▾ TAX_YEAR > RETURN_TYPE > TAX_PAYER`). Shown inside the
        per-submission pane so it only renders for the active tab.
      -->
      <div class="submission-breadcrumb">
        <span class="crumb">{{ submission.TAX_YEAR }}</span>
        <i class="fa fa-angle-right crumb-sep" aria-hidden="true"></i>
        <span class="crumb">{{ submission.RETURN_TYPE }}</span>
        <i class="fa fa-angle-right crumb-sep" aria-hidden="true"></i>
        <span class="crumb">{{ submission.TAX_PAYER }}</span>
      </div>
      <submission-detail
        [submission]="submission"
        [taxYear]="taxYear"
        [submissionType]="submissionType"
        [subState]="activeSubState"
        [childSubState]="activeChildSubState"
        (subStateChange)="handleSubStateChange($event)"
      ></submission-detail>
    </div>
}
  
}
</div>

<!--
  Confirm-action modal — port of
  `ui/app/templates/modals/confirm-action.html`. Triggered when a grid
  pencil icon (Return Error column) or a bulk-toolbar action dispatches
  through `EfileCtrlFnDispatcherService`. The modal fires `confirmed`
  → plan.execute() → grid refresh; `cancelled` just closes.

  NOTE: rendered outside the tab shell on purpose so it's never trapped
  inside a transformed ancestor. Even though dropping `<mat-tab-group>`
  removed the original animation transform, keeping the modal at the
  component root makes the backdrop layering robust to any future
  parent-level styling.
-->
@if (showConfirmModal) {
<gtw-confirmation-modal
 
  [config]="confirmModalConfig"
  [visible]="false"
  (confirmed)="onConfirmModal()"
  (cancelled)="onCancelModal()"
  (closed)="onCancelModal()"
></gtw-confirmation-modal>
}
